import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useCurrentUser } from './CurrentUserContext';

const InvoicesContext = createContext(null);

export const useInvoices = () => {
  const context = useContext(InvoicesContext);
  if (!context) throw new Error('useInvoices must be used within an InvoicesProvider');
  return context;
};

// Normalize API response (projectId camelCase) to frontend shape (project_id snake_case)
function normalizeInvoice(inv) {
  return {
    ...inv,
    id: Number(inv.id),
    project_id: inv.projectId ?? inv.project_id ?? null,
    linkedTaskId: inv.linkedTaskId != null ? Number(inv.linkedTaskId) : null,
    lineItems: inv.lineItems || [],
    reminders: inv.reminders || [],
    recurring: inv.recurring || null,
  };
}

export const InvoicesProvider = ({ children }) => {
  const { currentUser, activeWorkspaceId } = useCurrentUser();
  const [invoices, setInvoices] = useState([]);

  const refresh = useCallback(() => {
    api('/api/invoices')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.invoices)) setInvoices(data.invoices.map(normalizeInvoice));
      })
      .catch(err => console.error('Failed to load invoices:', err));
  }, []);

  // Refetch on workspace switch too — this provider wraps the AppContent remount
  // boundary, so it must key off activeWorkspaceId (mirrors ContactsContext).
  useEffect(() => {
    if (!currentUser?.id) return;
    refresh();
  }, [currentUser?.id, activeWorkspaceId, refresh]);

  useEffect(() => {
    window.addEventListener('aidey:invoicesUpdated', refresh);
    return () => window.removeEventListener('aidey:invoicesUpdated', refresh);
  }, [refresh]);

  return (
    <InvoicesContext.Provider value={{ invoices, setInvoices, refresh }}>
      {children}
    </InvoicesContext.Provider>
  );
};
